import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { MapPin, Calendar, Users, Award, Clock, Phone, Mail, Instagram, Linkedin, Youtube, MessageCircle } from 'lucide-react';
import { mockData } from '../mock';
import BookingModal from '../components/BookingModal';
import CoachingEnquiryModal from '../components/CoachingEnquiryModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LandingPage = () => {
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isCoachingEnquiryOpen, setIsCoachingEnquiryOpen] = useState(false);
  const [selectedCoaching, setSelectedCoaching] = useState('');
  const [isBrandingSubmitting, setIsBrandingSubmitting] = useState(false);
  const [isFranchiseSubmitting, setIsFranchiseSubmitting] = useState(false);

  const openBookingModal = (locationName = null) => {
    setSelectedLocation(locationName);
    setIsBookingModalOpen(true);
  };

  const closeBookingModal = () => {
    setIsBookingModalOpen(false);
    setSelectedLocation(null);
  };

  const openCoachingEnquiry = (coachingTitle = '') => {
    setSelectedCoaching(coachingTitle);
    setIsCoachingEnquiryOpen(true);
  };

  const closeCoachingEnquiry = () => {
    setIsCoachingEnquiryOpen(false);
    setSelectedCoaching('');
  };

  const scrollToBooking = () => {
    openBookingModal();
  };

  return (
    <div className="landing-page">
      {/* Header */}
      <header className="header">
        <div className="header-content">
          <div className="logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_ace-padel/artifacts/l64uaepk_Full%20lockup_3%404x-8.png" 
              alt="Space# Logo" 
              className="logo-image"
            />
          </div>
          <nav className="nav">
            <a href="#locations">Locations</a>
            <a href="#coaching">Coaching</a>
            <a href="#franchise">Franchise</a>
            <a href="#faq">FAQ</a>
          </nav>
          <Button onClick={scrollToBooking} className="book-btn">Book a Court</Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <h1 className="hero-title">
            Hash your game at
            <span className="brand-highlight"> Space# premium courts</span>
          </h1>
          <p className="hero-subtitle">
            Experience the world's fastest-growing sport at state-of-the-art facilities designed for players of all levels
          </p>
          <div className="hero-cta">
            <Button onClick={scrollToBooking} size="lg" className="primary-cta">Book a Court Now</Button>
            <a 
              href="https://play.google.com/store/apps/details?id=com.padelplay.piplay&hl=en_IN" 
              target="_blank" 
              rel="noopener noreferrer"
              className="app-store-btn"
            >
              <img 
                src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" 
                alt="Get it on Google Play" 
                className="app-badge"
              />
            </a>
            <a 
              href="https://apps.apple.com/in/app/pi-play-play-padel-pickleball/id6451375568" 
              target="_blank" 
              rel="noopener noreferrer"
              className="app-store-btn"
            >
              <img 
                src="https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg" 
                alt="Download on the App Store" 
                className="app-badge"
              />
            </a>
          </div>
        </div>
        <div className="hero-image-wrapper">
          <img 
            src="https://customer-assets.emergentagent.com/job_ace-padel/artifacts/93ujk2kh_image%20with%20customers.jpg" 
            alt="Space# Community" 
            className="hero-image"
          />
        </div>
      </section>

      {/* Premium Courts Section */}
      <section className="premium-section">
        <div className="section-header">
          <h2 className="section-title">World-Class Facilities</h2>
          <p className="section-description">
            Our courts feature cutting-edge design, premium materials, and optimal playing conditions
          </p>
        </div>
        <div className="features-grid">
          {mockData.features.map((feature, index) => (
            <Card key={index} className="feature-card">
              <CardHeader>
                <div className="feature-icon">{feature.icon}</div>
                <CardTitle>{feature.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription>{feature.description}</CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Locations Section */}
      <section id="locations" className="locations-section">
        <div className="section-header">
          <h2 className="section-title">Our Locations</h2>
          <p className="section-description">
            Premium padel facilities across multiple locations
          </p>
        </div>
        <div className="locations-grid">
          {mockData.locations.map((location, index) => (
            <Card key={index} className="location-card" data-testid={`location-card-${location.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
              <div className="location-image-wrapper">
                <img src={location.image} alt={location.name} className="location-image" />
              </div>
              <CardHeader>
                <CardTitle className="location-name">{location.name}</CardTitle>
                <CardDescription className="location-address">
                  <MapPin className="inline-icon" />
                  {location.address}
                </CardDescription>
                {location.addressLine2 && (
                  <CardDescription className="location-address-line2">
                    {location.addressLine2}
                  </CardDescription>
                )}
              </CardHeader>
              <CardContent>
                <p className="location-description">{location.description}</p>
                <div className="location-details">
                  <span className="detail-item">
                    <Clock className="detail-icon" />
                    {location.courts} Courts
                  </span>
                  <span className="detail-item">
                    {location.type}
                  </span>
                </div>
                {location.comingSoon ? (
                  <Button className="location-btn coming-soon-btn" disabled>
                    Coming Soon
                  </Button>
                ) : (
                  <Button className="location-btn" data-testid={`book-now-${location.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} onClick={() => openBookingModal(location.name)}>
                    Book Now
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Coaching Section */}
      <section id="coaching" className="coaching-section">
        <div className="section-header">
          <h2 className="section-title">Professional Coaching</h2>
          <p className="section-description">
            Learn, grow, and play with certified padel coaches dedicated to helping you improve your skills, boost confidence, and build a lifelong passion for the game.
          </p>
        </div>
        <div className="coaching-image-banner">
          <img 
            src="https://images.unsplash.com/photo-1646649853703-7645147474ba" 
            alt="Professional padel coaching" 
            className="coaching-banner-img"
          />
        </div>
        <div className="coaching-cards-container">
          {mockData.coachingOptions.map((option, index) => (
            <Card key={index} className="coaching-option-card">
              <CardHeader>
                <CardTitle className="coaching-option-title">{option.title}</CardTitle>
                <CardDescription className="coaching-option-desc">{option.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="coaching-option-features">
                  {option.features.map((feat, idx) => (
                    <li key={idx} className="coaching-option-feature">{feat}</li>
                  ))}
                </ul>
                <Button className="coaching-option-btn" onClick={() => openCoachingEnquiry(option.title)}>
                  Enquire
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="why-choose-section">
        <div className="section-header">
          <h2 className="section-title">Why Choose space#?</h2>
        </div>
        <div className="benefits-grid">
          {mockData.benefits.map((benefit, index) => (
            <div key={index} className="benefit-item">
              <div className="benefit-icon-wrapper">
                {benefit.icon}
              </div>
              <h3 className="benefit-title">{benefit.title}</h3>
              <p className="benefit-description">{benefit.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Branding Opportunities Section */}
      <section className="branding-section">
        <div className="branding-container">
          <div className="branding-content">
            <span className="branding-tag">Branding Opportunity</span>
            <h3 className="branding-title">Reach Active, Affluent Audiences</h3>
            <p className="branding-description">
              Showcase your brand at our premium facilities with high footfall. Prime advertising spaces available.
            </p>
          </div>
          <div className="branding-form-compact">
            <form 
              className="branding-form-inline"
              onSubmit={async (e) => {
                e.preventDefault();
                setIsBrandingSubmitting(true);
                const formData = new FormData(e.target);
                const data = {
                  name: formData.get('brand-name'),
                  email: formData.get('brand-email'),
                  phone: formData.get('brand-phone')
                };
                
                try {
                  const response = await fetch(`${BACKEND_URL}/api/branding-enquiry`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(data),
                  });

                  if (response.ok) {
                    alert(`✅ Branding Enquiry Submitted!\n\nYour details have been sent to info@spacehash.club\n\nName: ${data.name}\nEmail: ${data.email}\nPhone: ${data.phone}\n\nOur team will contact you within 24 hours.`);
                    e.target.reset();
                  } else {
                    throw new Error('Failed to submit');
                  }
                } catch (error) {
                  alert('⚠️ There was an error submitting your enquiry. Please contact us at info@spacehash.club.');
                } finally {
                  setIsBrandingSubmitting(false);
                }
              }}
            >
              <input
                type="text"
                name="brand-name"
                placeholder="Company Name"
                className="branding-input-compact"
                required
              />
              <input
                type="email"
                name="brand-email"
                placeholder="Email"
                className="branding-input-compact"
                required
              />
              <input
                type="tel"
                name="brand-phone"
                placeholder="Phone"
                className="branding-input-compact"
                required
              />
              <Button type="submit" className="branding-submit-compact" disabled={isBrandingSubmitting}>
                {isBrandingSubmitting ? 'Submitting...' : 'Submit'}
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* Franchise Section */}
      <section id="franchise" className="franchise-section-new">
        <div className="franchise-header">
          <span className="franchise-badge">Partnership Opportunity</span>
          <h2 className="franchise-main-title">Join the Padel Revolution</h2>
          <p className="franchise-main-subtitle">Partner with India's fastest-growing premium padel network</p>
        </div>

        <div className="franchise-stats">
          <div className="stat-card">
            <div className="stat-number">10+</div>
            <div className="stat-label">Locations Planned</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">₹50L+</div>
            <div className="stat-label">Monthly Revenue Potential</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">18</div>
            <div className="stat-label">Months ROI Period</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">100%</div>
            <div className="stat-label">Support & Training</div>
          </div>
        </div>

        <div className="franchise-main-container">
          <div className="franchise-left">
            <div className="franchise-vision">
              <h3 className="franchise-section-title">Why Space# Franchise?</h3>
              <p className="franchise-vision-text">
                Be part of the fastest-growing sport in India. Space# is revolutionizing padel and pickle sports with world-class facilities, cutting-edge technology, and a thriving community. Our proven business model and comprehensive support system ensure your success from day one.
              </p>
            </div>

            <div className="franchise-benefits-new">
              <h3 className="franchise-section-title">What You Get</h3>
              <div className="benefits-grid-new">
                {mockData.franchise.benefits.map((benefit, index) => (
                  <div key={index} className="benefit-card-new">
                    <div className="benefit-icon-new">✓</div>
                    <span className="benefit-text-new">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="franchise-right">
            <Card className="franchise-form-card-new">
              <CardHeader>
                <CardTitle className="form-card-title">Start Your Journey</CardTitle>
                <CardDescription className="form-card-description">
                  Fill in your details and our partnership team will contact you within 24 hours
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form className="franchise-form-new" onSubmit={async (e) => {
                  e.preventDefault();
                  setIsFranchiseSubmitting(true);
                  const formData = new FormData(e.target);
                  const data = {
                    name: formData.get('name'),
                    email: formData.get('email'),
                    phone: formData.get('phone'),
                    city: formData.get('city'),
                    message: formData.get('message')
                  };
                  
                  try {
                    const response = await fetch(`${BACKEND_URL}/api/franchise-enquiry`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify(data),
                    });

                    if (response.ok) {
                      alert(`✅ Franchise Enquiry Submitted!\n\nYour details have been sent to info@spacehash.club\n\nDetails:\nName: ${data.name}\nEmail: ${data.email}\nPhone: ${data.phone}\nCity: ${data.city || 'Not specified'}\n\nOur franchise team will contact you within 24 hours to discuss this exciting opportunity!`);
                      e.target.reset();
                    } else {
                      throw new Error('Failed to submit');
                    }
                  } catch (error) {
                    alert('⚠️ There was an error submitting your enquiry. Please contact us at info@spacehash.club.');
                  } finally {
                    setIsFranchiseSubmitting(false);
                  }
                }}>
                  <div className="form-row">
                    <div className="form-group-new">
                      <label htmlFor="franchise-name" className="form-label-new">Full Name *</label>
                      <input 
                        type="text" 
                        id="franchise-name" 
                        name="name" 
                        className="form-input-new" 
                        required 
                        placeholder="John Doe"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group-new">
                      <label htmlFor="franchise-email" className="form-label-new">Email Address *</label>
                      <input 
                        type="email" 
                        id="franchise-email" 
                        name="email" 
                        className="form-input-new" 
                        required 
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group-new">
                      <label htmlFor="franchise-phone" className="form-label-new">Phone Number *</label>
                      <input 
                        type="tel" 
                        id="franchise-phone" 
                        name="phone" 
                        className="form-input-new" 
                        required 
                        placeholder="+91 98765 43210"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group-new">
                      <label htmlFor="franchise-city" className="form-label-new">Preferred City *</label>
                      <input 
                        type="text" 
                        id="franchise-city" 
                        name="city" 
                        className="form-input-new" 
                        required
                        placeholder="e.g., Bangalore, Mumbai"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group-new">
                      <label htmlFor="franchise-message" className="form-label-new">Tell us about yourself</label>
                      <textarea 
                        id="franchise-message" 
                        name="message" 
                        className="form-textarea-new" 
                        rows={3}
                        placeholder="Your business background, experience, and why you're interested..."
                      ></textarea>
                    </div>
                  </div>

                  <Button type="submit" className="franchise-submit-btn-new" disabled={isFranchiseSubmitting}>
                    {isFranchiseSubmitting ? 'Submitting...' : 'Submit Partnership Enquiry'}
                  </Button>
                  
                  <p className="form-privacy-note">
                    Your information will be sent to <strong>info@spacehash.club</strong> and kept confidential
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="faq-section">
        <div className="section-header">
          <h2 className="section-title">Frequently Asked Questions</h2>
        </div>
        <div className="faq-content">
          <Accordion type="single" collapsible className="faq-accordion">
            {mockData.faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="faq-question">{faq.question}</AccordionTrigger>
                <AccordionContent className="faq-answer">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-content">
          <h2 className="cta-title">Join the Action</h2>
          <p className="cta-subtitle">Be part of India's fastest-growing padel community</p>
          
          <div className="cta-image-strip">
            <div className="cta-image-box">
              <img src="https://customer-assets.emergentagent.com/job_ace-padel/artifacts/cplq6dol_team%20photo.jpg" alt="Space# Team Photo" className="cta-image" />
            </div>
            <div className="cta-image-box">
              <img src="https://customer-assets.emergentagent.com/job_ace-padel/artifacts/mvdi8kf9_WhatsApp%20Image%202025-11-03%20at%2016.05.53_f61beec0.jpg" alt="Space# Tournament" className="cta-image" />
            </div>
            <div className="cta-image-box">
              <img src="https://customer-assets.emergentagent.com/job_ace-padel/artifacts/kwbpy2ur_Screenshot%202025-11-03%20161416.png" alt="Space# Monthly Coaching" className="cta-image" />
            </div>
            <div className="cta-image-box">
              <img src="https://customer-assets.emergentagent.com/job_ace-padel/artifacts/pcjh3kqc_WhatsApp%20Image%202025-11-03%20at%2016.18.10_be2a5efd.jpg" alt="Space# Community" className="cta-image" />
            </div>
          </div>
          
          <Button size="lg" className="cta-button" onClick={scrollToBooking}>
            Book Your Court Now
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-section">
            <h3 className="footer-logo">space#</h3>
            <p className="footer-tagline">where recreation meets community, inspiring an active lifestyle through innovative padel and pickle sports in India through world class facilities</p>
          </div>
          <div className="footer-section">
            <h4 className="footer-heading">Quick Links</h4>
            <ul className="footer-links">
              <li><a href="#locations">Locations</a></li>
              <li><a href="#coaching">Coaching</a></li>
              <li><a href="#about">About Us</a></li>
              <li><a href="#faq">FAQ</a></li>
            </ul>
          </div>
          <div className="footer-section">
            <h4 className="footer-heading">Contact</h4>
            <ul className="footer-contact">
              <li>
                <Phone className="footer-icon" />
                <span>+91 98956 04656</span>
              </li>
              <li>
                <Phone className="footer-icon" />
                <span>+91 99457 10765</span>
              </li>
              <li>
                <Mail className="footer-icon" />
                <span>info@spacehash.club</span>
              </li>
            </ul>
            <div className="social-links">
              <a href="https://www.instagram.com/spacehash_kochi?igsh=MTJ1MTNpZ3YxYWkxMg==" target="_blank" rel="noopener noreferrer" className="social-link" aria-label="Instagram">
                <Instagram className="social-icon" />
              </a>
              <a href="#" className="social-link" aria-label="LinkedIn">
                <Linkedin className="social-icon" />
              </a>
              <a href="#" className="social-link" aria-label="YouTube">
                <Youtube className="social-icon" />
              </a>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <div className="footer-partners">
            <span className="partners-label">Our Partners:</span>
            <div className="partner-logos">
              <img src="https://upload.wikimedia.org/wikipedia/commons/2/20/Adidas_Logo.svg" alt="Adidas" className="partner-logo" />
              <img src="https://customer-assets.emergentagent.com/job_ace-padel/artifacts/4h02j2cs_Babolat-Logo.png" alt="Babolat" className="partner-logo" />
              <img src="https://customer-assets.emergentagent.com/job_ace-padel/artifacts/yyt1y3t0_piplay_logo.webp" alt="PiPlay" className="partner-logo" />
              <span className="partner-logo partner-text">SKYPADEL</span>
            </div>
          </div>
          <p className="footer-copyright">&copy; 2025 space#. All rights reserved.</p>
        </div>
      </footer>

      {/* Booking Modal */}
      <BookingModal 
        isOpen={isBookingModalOpen} 
        onClose={closeBookingModal}
        preselectedLocation={selectedLocation}
      />

      {/* Coaching Enquiry Modal */}
      <CoachingEnquiryModal 
        isOpen={isCoachingEnquiryOpen} 
        onClose={closeCoachingEnquiry}
        selectedCoaching={selectedCoaching}
      />

      {/* WhatsApp Widget */}
      <a 
        href="https://wa.me/919895604656?text=Hi%2C%20I%20would%20like%20to%20know%20more%20about%20Space%23%20Padel" 
        target="_blank" 
        rel="noopener noreferrer"
        className="whatsapp-widget"
        aria-label="Chat on WhatsApp"
      >
        <MessageCircle className="whatsapp-icon" />
      </a>
    </div>
  );
};

export default LandingPage;